/* Version: 3.5.149 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import 'react';
import { OverlayProvider, SSRProvider } from 'react-aria';
import PropTypes from 'prop-types';
import ToastProvider from '../Toast/ToastProvider.js';
import LANG from '../../lang/lang.js';
import LangProvider from '../../lang/langProvider.js';
import NavigationProvider from '../../navigation/NavigationProvider.js';
import { THEMES } from '../../theme/index.js';
import { isReact18 } from '../../utils/functions.js';
import { jsx } from 'react/jsx-runtime';
import ThemeProvider from '../../theme/ThemeProvider.js';

var SSRProviderWrapper = function SSRProviderWrapper(_ref) {
  var children = _ref.children;
  return (
    // SSRProvider is required only for older React apps and on the server.
    // React Aria now uses the new SSR-safe `useId` hook if using React 18, so the provider is not required.
    // @see https://react-spectrum.adobe.com/react-aria/SSRProvider.html
    isReact18() ? children : /*#__PURE__*/jsx(SSRProvider, {
      children: children
    })
  );
};
var MuxProvider = function MuxProvider(_ref2) {
  var children = _ref2.children,
    _ref2$langProvider = _ref2.langProvider,
    langProvider = _ref2$langProvider === undefined ? {
      lang: LANG.EN_CA
    } : _ref2$langProvider,
    _ref2$navigationProvi = _ref2.navigationProvider,
    navigationProvider = _ref2$navigationProvi === undefined ? {
      isSignedIn: false
    } : _ref2$navigationProvi,
    _ref2$notificationPro = _ref2.notificationProvider,
    notificationProvider = _ref2$notificationPro === undefined ? {
      interval: 4000
    } : _ref2$notificationPro,
    _ref2$overlayProvider = _ref2.overlayProvider,
    overlayProvider = _ref2$overlayProvider === undefined ? {} : _ref2$overlayProvider,
    _ref2$themeProvider = _ref2.themeProvider,
    themeProvider = _ref2$themeProvider === undefined ? {
      theme: THEMES.canadianTheme
    } : _ref2$themeProvider;
  return /*#__PURE__*/jsx(ThemeProvider, _objectSpread(_objectSpread({}, themeProvider), {}, {
    children: /*#__PURE__*/jsx(LangProvider, _objectSpread(_objectSpread({}, langProvider), {}, {
      children: /*#__PURE__*/jsx(NavigationProvider, _objectSpread(_objectSpread({}, navigationProvider), {}, {
        children: /*#__PURE__*/jsx(OverlayProvider, _objectSpread(_objectSpread({}, overlayProvider), {}, {
          style: {},
          children: /*#__PURE__*/jsx(SSRProviderWrapper, {
            children: /*#__PURE__*/jsx(ToastProvider, _objectSpread(_objectSpread({}, notificationProvider), {}, {
              children: children
            }))
          })
        }))
      }))
    }))
  }));
};
process.env.NODE_ENV !== "production" ? MuxProvider.propTypes = {
  /**
   * Children components
   */
  children: PropTypes.node.isRequired,
  /**
   * See [styled-components' `ThemeProvider`](https://styled-components.com/docs/advanced)
   */
  themeProvider: PropTypes.shape({
    theme: PropTypes.object
  }),
  /**
   * See [LangProvider](/docs/lang-provider)
   */
  langProvider: PropTypes.shape({
    lang: PropTypes.oneOf([LANG.EN_CA, LANG.FR_CA])
  }),
  /**
   * See [NavigationProvider](/docs/navigation-provider)
   */
  navigationProvider: PropTypes.shape({
    isSignedIn: PropTypes.bool
  }),
  /**
   * See [react-aria's `useDialog`](https://react-spectrum.adobe.com/react-aria/useDialog.html)
   */
  overlayProvider: PropTypes.object,
  /**
   * See [Toast](/docs/toast)
   */
  notificationProvider: PropTypes.shape({
    interval: PropTypes.number
  })
} : undefined;
process.env.NODE_ENV !== "production" ? MuxProvider.displayName = 'MuxProvider' : undefined;

export { MuxProvider as default };
